import React, { useState, useRef, useCallback, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SyntaxHighlightInput from './SyntaxHighlightInput';
import OsScroll from './OsScroll';

const HEADER_KEYS = [
  'Accept',
  'Accept-Encoding',
  'Accept-Language',
  'Access-Control-Allow-Credentials',
  'Access-Control-Allow-Headers',
  'Access-Control-Allow-Methods',
  'Access-Control-Allow-Origin',
  'Access-Control-Expose-Headers',
  'Access-Control-Max-Age',
  'Access-Control-Request-Headers',
  'Access-Control-Request-Method',
  'Authorization',
  'Cache-Control',
  'Connection',
  'Content-Disposition',
  'Content-Encoding',
  'Content-Language',
  'Content-Length',
  'Content-Type',
  'Cookie',
  'DNT',
  'Forwarded',
  'Host',
  'If-Match',
  'If-Modified-Since',
  'If-None-Match',
  'If-Range',
  'If-Unmodified-Since',
  'Origin',
  'Pragma',
  'Range',
  'Referer',
  'Sec-CH-UA',
  'Sec-CH-UA-Mobile',
  'Sec-CH-UA-Platform',
  'Sec-Fetch-Dest',
  'Sec-Fetch-Mode',
  'Sec-Fetch-Site',
  'Sec-Fetch-User',
  'Upgrade-Insecure-Requests',
  'User-Agent',
  'X-API-Key',
  'X-Correlation-ID',
  'X-CSRF-Token',
  'X-Forwarded-For',
  'X-Forwarded-Host',
  'X-Forwarded-Port',
  'X-Forwarded-Proto',
  'X-Forwarded-Server',
  'X-Real-IP',
  'X-Request-ID',
  'X-Requested-With',
  'X-Trace-ID',
];

const MAX_VISIBLE = 8;

function HighlightedMatch({ text, query }) {
  if (!query || !text.toLowerCase().startsWith(query.toLowerCase())) {
    return <span>{text}</span>;
  }
  return (
    <>
      <strong className="header-key-item-match">{text.slice(0, query.length)}</strong>
      <span>{text.slice(query.length)}</span>
    </>
  );
}

export default function HeaderKeyInput({ value, onChange, onBlur: onBlurProp, ...props }) {
  const [isFocused, setIsFocused] = useState(false);
  const [suppressed, setSuppressed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [forceSyncNonce, setForceSyncNonce] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 220 });
  const containerRef = useRef(null);

  // Empty field: show the full recommendation list on focus. Once the user types,
  // narrow to prefix matches (excluding an exact match, which needs no suggestion).
  const filtered = value
    ? HEADER_KEYS.filter(
        (h) =>
          h.toLowerCase().startsWith(value.toLowerCase()) &&
          h.toLowerCase() !== value.toLowerCase()
      )
    : HEADER_KEYS;

  // Empty field → full browseable list (dropdown scrolls). Typing → tight prefix list.
  const visible = value ? filtered.slice(0, MAX_VISIBLE) : filtered;
  const showDropdown = isFocused && visible.length > 0 && !suppressed;

  const measure = useCallback(() => {
    if (!containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 220) });
  }, []);

  useLayoutEffect(() => {
    if (!showDropdown) return;
    measure();
  }, [showDropdown, value, measure]);

  useEffect(() => {
    if (!showDropdown) return;
    const onResizeScroll = () => measure();
    window.addEventListener('resize', onResizeScroll);
    window.addEventListener('scroll', onResizeScroll, true);
    return () => {
      window.removeEventListener('resize', onResizeScroll);
      window.removeEventListener('scroll', onResizeScroll, true);
    };
  }, [showDropdown, measure]);

  useEffect(() => {
    setActiveIndex(-1);
    setSuppressed(false);
  }, [value]);

  const pick = useCallback(
    (header) => {
      onChange({ target: { value: header } });
      setForceSyncNonce((n) => n + 1);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!showDropdown) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, -1));
      } else if ((e.key === 'Enter' || e.key === 'Tab') && (activeIndex >= 0 || visible.length === 1)) {
        e.preventDefault();
        pick(visible[activeIndex >= 0 ? activeIndex : 0]);
      } else if (e.key === 'Escape') {
        setSuppressed(true);
      }
    },
    [showDropdown, visible, activeIndex, pick]
  );

  const dropdown =
    showDropdown && typeof document !== 'undefined' && document.body
      ? createPortal(
          <>
            <div
              className="header-key-backdrop"
              aria-hidden
              onMouseDown={(e) => {
                e.preventDefault();
                setSuppressed(true);
              }}
            />
            <div
              className="header-key-dropdown"
              role="listbox"
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                minWidth: pos.width,
                zIndex: 10040,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <OsScroll className="header-key-dropdown-scroll">
                {visible.map((h, i) => (
                  <button
                    key={h}
                    type="button"
                    role="option"
                    aria-selected={i === activeIndex}
                    className={`header-key-item ${i === activeIndex ? 'header-key-item--active' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(h)}
                  >
                    <HighlightedMatch text={h} query={value} />
                  </button>
                ))}
              </OsScroll>
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <div
      ref={containerRef}
      // Clicking outside suppresses the dropdown (via the backdrop) while keeping focus,
      // so re-interacting with the field must clear `suppressed` to reopen it — a plain
      // focus event won't fire when focus was never lost. Guard on the target being inside
      // this field: the backdrop is portaled, so its mousedown bubbles here through the React
      // tree and would otherwise clear `suppressed` immediately after it set it (dropdown
      // would never close on outside click).
      onMouseDown={(e) => {
        if (containerRef.current?.contains(e.target)) setSuppressed(false);
      }}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget)) {
          setIsFocused(false);
          onBlurProp?.(e);
        }
      }}
      onKeyDown={handleKeyDown}
    >
      <SyntaxHighlightInput value={value} onChange={onChange} forceSyncNonce={forceSyncNonce} {...props} />
      {dropdown}
    </div>
  );
}
